import React, { useRef, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import styles from "./FramerWrapperArticle.module.scss";
import { API_BASE_URL } from "../../config/index";
import IconClose from "../../common/icons/IconClose";
import IconAuthorPlaceholder from "../../common/icons/IconAuthorPlaceholder";
import IconDot from "../../common/icons/IconDot";
import ArticleSkeleton from "./ArticleSkeleton";
import { formatDate } from "../../utils/dateUtils";
import {
  getByLanguage,
  getAuthorName,
  processArticleContent,
} from "../../utils/articleUtils";
import { languageConfig } from "../../utils/languageConfig";

const fetchArticleById = async (articleId, token) => {
  const response = await fetch(
    `${API_BASE_URL}/blog/article?id=${articleId}&fields=*,content,author,chains,category,type,main_media,tags,organization,relatedArticles&access_token=${token}`
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { data: articles } = await response.json();

  if (articles?.length) {
    return articles[0];
  } else {
    throw new Error("Article not found in the response");
  }
};

const FrameWrapperArticle = ({ id, token, closePopup }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [articleData, setArticleData] = useState(null);
  const [processedContent, setProcessedContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState("fr");

  useEffect(() => {
    const handleScroll = (e) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
      }
      e.stopPropagation();
    };

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (container) {
        container.addEventListener("wheel", handleScroll, { passive: false });
      } else {
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      const container = containerRef.current;
      if (container) {
        container.removeEventListener("wheel", handleScroll);
      }
    };
  }, [articleData]); // Depend on articleData so it reattaches when content loads

  useEffect(() => {
    const observers = [];
    if (contentRef.current && processedContent) {
      const tables = contentRef.current.querySelectorAll("table");
      tables.forEach((table) => {
        if (table.dataset.wrapped === "true") {
          return;
        }
        table.dataset.wrapped = "true";

        const wrapper = document.createElement("div");
        wrapper.className = styles.tableWrapper;

        const prompt = document.createElement("p");
        prompt.className = styles.scrollPrompt;

        const indicator = document.createElement("div");
        indicator.className = styles.scrollIndicator;
        const arrow = document.createElement("span");
        arrow.textContent = "→";
        indicator.appendChild(arrow);

        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);

        wrapper.parentNode.insertBefore(prompt, wrapper);

        wrapper.appendChild(indicator);

        const handleFirstScroll = () => {
          wrapper.classList.add(styles.scrollingStarted);
          wrapper.removeEventListener("scroll", handleFirstScroll);
        };

        const observer = new ResizeObserver(([entry]) => {
          const isOverflowing =
            entry.target.scrollWidth > entry.target.clientWidth;

          prompt.style.display = isOverflowing ? "block" : "none";

          wrapper.removeEventListener("scroll", handleFirstScroll);

          if (isOverflowing) {
            entry.target.classList.add(styles.isScrollable);
            entry.target.classList.remove(styles.scrollingStarted);
            wrapper.addEventListener("scroll", handleFirstScroll);
          } else {
            entry.target.classList.remove(styles.isScrollable);
          }
        });
        observer.observe(wrapper);
        observers.push({ observer, wrapper, handleScroll: handleFirstScroll });
      });
    }

    return () => {
      observers.forEach(({ observer, wrapper, handleScroll }) => {
        observer.disconnect();
        wrapper.removeEventListener("scroll", handleScroll);
      });
    };
  }, [processedContent]);

  useEffect(() => {
    const loadInitialArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        const article = await fetchArticleById(id, token);
        setArticleData(article);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      loadInitialArticle();
    }
  }, [id, token]);

  useEffect(() => {
    if (articleData?.language) {
      setCurrentLanguage(articleData.language);
    }
  }, [articleData]);

  useEffect(() => {
    if (articleData?.content) {
      const newContent = processArticleContent(articleData.content);
      setProcessedContent(newContent);
    }
  }, [articleData]);

  const handleContentClick = (e) => {
    // Check if the clicked element is a link
    if (e.target.tagName === "A" || e.target.closest("a")) {
      const link = e.target.tagName === "A" ? e.target : e.target.closest("a");
      const href = link.getAttribute("href");

      if (href) {
        // Check if the link contains an anchor (# fragment)
        const hashIndex = href.indexOf("#");

        if (hashIndex !== -1) {
          // It's a ToC link, open in a new tab.
          e.preventDefault();
          window.open(href, "_blank", "noopener,noreferrer");
        }
        // For external links without anchors, open in new tab
        else if (href.startsWith("http://") || href.startsWith("https://")) {
          e.preventDefault();
          window.open(href, "_blank", "noopener,noreferrer");
        }
      }
    }
  };

  if (loading) {
    return <ArticleSkeleton closePopup={closePopup} ref={containerRef} />;
  }

  if (error || !articleData) {
    return (
      <>
        <div className={styles.modalBackdrop} onClick={closePopup} />
        <div className={styles.modalWrapper} data-article-modal>
          <div className={styles.articleContainer} ref={containerRef}>
            <div className={styles.error}>
              Erreur lors du chargement de l'article: {error}
            </div>
          </div>
          <div className={styles.closeIcon} onClick={closePopup}>
            <IconClose />
          </div>
        </div>
      </>
    );
  }

  const {
    author: authors = [],
    chains,
    category,
    type,
    organization,
    tags = [],
    title,
    publishedAt,
    readTime,
    relatedArticles,
    main_media,
  } = articleData;

  const chain = chains?.[0];
  const chainAvatar =
    chain?.mediaChain?.find((media) => media.language === currentLanguage)
      ?.avatarUrl || chain?.mediaChain?.[0]?.avatarUrl;

  // Define available languages based on relatedArticles field and current language
  const getAvailableLanguages = () => {
    const languages = [currentLanguage]; // Always include current language

    // Add languages from relatedArticles
    if (relatedArticles) {
      Object.keys(relatedArticles).forEach((lang) => {
        if (!languages.includes(lang)) {
          languages.push(lang);
        }
      });
    }

    // Sort languages for consistent order: fr, en, nl
    const languageOrder = ["fr", "en", "nl"];
    return languages.sort((a, b) => {
      return languageOrder.indexOf(a) - languageOrder.indexOf(b);
    });
  };

  const availableLanguages = getAvailableLanguages();

  // Helper function to get tag name in current language
  const getTagName = (tag, language) => {
    return getByLanguage(tag, "name", language) || "Unknown";
  };

  // Helper function to get section title in current language
  const getKeywordsTitle = (language) => {
    switch (language) {
      case "en":
        return "Keywords";
      case "nl":
        return "Kernwoorden";
      case "fr":
      default:
        return "Mots clés";
    }
  };

  // Helper function to get meta text in current language
  const getMetaText = (language) => {
    switch (language) {
      case "en":
        return {
          published: "Published",
          reading: "Reading",
        };
      case "nl":
        return {
          published: "Gepubliceerd",
          reading: "Lezen",
        };
      case "fr":
      default:
        return {
          published: "Publié",
          reading: "Lecture de",
        };
    }
  };

  const metaText = getMetaText(currentLanguage);

  // Helper function to get category name in current language
  const getCategoryName = (category, language) => {
    return getByLanguage(category, "name", language) || "";
  };

  // Helper function to get type name in current language
  const getTypeName = (type, language) => {
    return getByLanguage(type, "name", language) || "";
  };

  // Handle language flag click
  const handleLanguageChange = async (targetLanguage) => {
    if (targetLanguage === currentLanguage) {
      return; // Already viewing this language
    }

    // Check if target language exists in relatedArticles
    const relatedArticle = relatedArticles?.[targetLanguage];
    if (!relatedArticle) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const article = await fetchArticleById(relatedArticle.id, token);
      setArticleData(article);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.modalBackdrop} onClick={closePopup} />
      <div className={styles.modalWrapper} data-article-modal>
        <div className={styles.articleContainer} ref={containerRef}>
          {/* Header with background image */}
          <div className={styles.articleHeader}>
            <div className={styles.headerBackground}>
              <img
                src={main_media?.fullMediaUrl || "/assets/typewriter-bg.jpg"}
                alt="Background"
                className={styles.backgroundImage}
                onError={(e) => {
                  e.target.src = "/assets/typewriter-bg.jpg";
                }}
              />
            </div>

            <div className={styles.articleMetadata}>
              <div className={styles.headerTop}>
                <div className={styles.articleTags}>
                  <div className={styles.articleTagRow}>
                    <span className={`${styles.tag} ${styles.tagWhite}`}>
                      ARTICLE
                    </span>
                  </div>
                  <div className={styles.categoryTagRow}>
                    <span
                      className={`${styles.tag} ${styles.tagCategory}`}
                      style={{ backgroundColor: category?.colorCode }}
                    >
                      {getCategoryName(category, currentLanguage)}
                    </span>
                    <span className={styles.typeText}>
                      {getTypeName(type, currentLanguage)}
                    </span>
                    <span
                      className={styles.separator}
                      style={{ backgroundColor: category?.colorCode }}
                    ></span>
                    {organization?.abbreviation && (
                      <span className={styles.typeText}>
                        {organization.abbreviation}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.chainLogo}>
                  {chainAvatar && (
                    <img
                      src={chainAvatar}
                      alt={chain?.nameFr || chain?.nameEn || chain?.nameNl}
                      className={styles.chainLogoImage}
                    />
                  )}
                </div>
              </div>

              <div className={styles.headerBottom}>
                <h1 className={styles.articleTitle}>{title}</h1>
                <div className={styles.articleMeta}>
                  <span>
                    {metaText.published} {formatDate(publishedAt)}
                  </span>
                  <IconDot />
                  <span>
                    {metaText.reading} {readTime || 3}min
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Author Section */}
          <div className={styles.authorSection}>
            <div className={styles.authorMobileWrapper}>
              <div className={styles.authorDetailsColumn}>
                {authors.length > 0
                  ? authors
                      .filter((author) => author?.enableAvatar !== false)
                      .map((author, index) => (
                        <div key={index} className={styles.authorCard}>
                          <div className={styles.authorAvatar}>
                            {author?.enableAvatar && author?.avatarUrl ? (
                              <img
                                src={author.avatarUrl}
                                alt={`${author.firstName} ${author.lastName}`}
                                className={styles.avatarImage}
                              />
                            ) : (
                              <IconAuthorPlaceholder />
                            )}
                          </div>
                          <div className={styles.authorInfo}>
                            <h3 className={styles.authorName}>
                              {getAuthorName(author)}
                            </h3>
                            {author?.signature?.head && (
                              <p className={styles.authorTitle}>
                                {author?.signature?.head}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                  : null}
              </div>
              <div className={styles.flagsColumn}>
                <div className={styles.languageFlags}>
                  {availableLanguages.map((lang) => {
                    const { Icon, label } = languageConfig[lang] || {};
                    if (!Icon) return null;

                    return (
                      <div
                        key={lang}
                        className={`${styles.flagContainer} ${
                          currentLanguage === lang ? styles.active : ""
                        }`}
                      >
                        <button
                          className={styles.flag}
                          onClick={() => handleLanguageChange(lang)}
                          aria-label={label}
                        >
                          <Icon />
                        </button>
                        <div className={styles.activeIndicator} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.dividerContainer}>
            <div className={styles.divider} />
          </div>

          {/* Content Area */}
          <div className={styles.articleContent}>
            <div
              ref={contentRef}
              className={styles.htmlContent}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(processedContent),
              }}
              onClick={handleContentClick}
            />
          </div>

          {/* Footer with Tags */}
          {tags.length > 0 && (
            <div className={styles.articleFooter}>
              <div className={styles.tagsSection}>
                <h3 className={styles.tagsTitle}>
                  {getKeywordsTitle(currentLanguage)}
                </h3>
                <div className={styles.tagsContainer}>
                  {tags
                    .filter(
                      (tag, index, self) =>
                        index === self.findIndex((t) => t.id === tag.id)
                    )
                    .map((tag) => (
                      <span key={tag.id} className={styles.keywordTag}>
                        {getTagName(tag, currentLanguage)}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.closeIcon} onClick={closePopup}>
          <IconClose />
        </div>
      </div>
    </>
  );
};

export default FrameWrapperArticle;
